import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Reveal, RevealLines } from "../components/Reveal";
import { Search, MapPin, ArrowUpRight } from "lucide-react";

const locations = ["all", "Remote", "Hybrid", "On-site"];

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [location, setLocation] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/jobs/meta/categories").then((r) => setCategories(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/jobs", { params: { q: q || undefined, category, location } })
        .then((r) => setJobs(r.data)).catch(() => {}).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, category, location]);

  return (
    <div className="bg-cream min-h-screen pt-[72px]" data-testid="jobs-page">
      {/* Header */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-10">
        <span className="overline text-cobalt">Curated opportunities</span>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-navy mt-5 leading-[0.95]">
          <RevealLines lines={["Roles worth", "chasing."]} />
        </h1>
        <p className="text-slateink text-lg mt-6 max-w-xl">
          Hand-picked openings for technical and professional talent. Apply directly — our team fast-tracks Joy clients.
        </p>
      </div>

      {/* Filters */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-10">
        <div className="bg-white border border-border p-4 flex flex-col lg:flex-row gap-3">
          <div className="flex items-center gap-3 flex-1 border border-border px-4 py-2.5">
            <Search size={18} className="text-slateink" />
            <input data-testid="jobs-search-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search role, company, skill…" className="w-full outline-none bg-transparent text-navy placeholder:text-slateink/60" />
          </div>
          <select data-testid="jobs-category-select" value={category} onChange={(e) => setCategory(e.target.value)}
            className="border border-border px-4 py-2.5 bg-white text-navy outline-none">
            <option value="all">All categories</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="jobs-location-select" value={location} onChange={(e) => setLocation(e.target.value)}
            className="border border-border px-4 py-2.5 bg-white text-navy outline-none">
            {locations.map((l) => <option key={l} value={l}>{l === "all" ? "Any location" : l}</option>)}
          </select>
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-28">
        <p className="text-sm text-slateink mb-5" data-testid="jobs-count">{loading ? "Loading…" : `${jobs.length} open roles`}</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {jobs.map((job, i) => (
            <Reveal key={job.id} delay={(i % 3) * 0.06}>
              <Link to={`/jobs/${job.id}`} data-testid={`job-card-${job.id}`}
                className="group block h-full bg-white border border-border p-6 hover:border-navy transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <span className="font-mono-ui text-[0.6rem] uppercase tracking-widest text-cobalt border border-cobalt/30 px-2 py-1">{job.category}</span>
                  <ArrowUpRight size={18} className="text-slateink group-hover:text-cobalt transition-colors" />
                </div>
                <h3 className="font-display text-2xl font-semibold text-navy mt-5 leading-tight">{job.title}</h3>
                <p className="text-slateink mt-1">{job.company}</p>
                <p className="text-sm text-slateink/90 mt-4 leading-relaxed line-clamp-2">{job.summary}</p>
                <div className="flex items-center gap-4 mt-6 pt-5 border-t border-border text-sm text-navy/80">
                  <span className="flex items-center gap-1.5"><MapPin size={14} />{job.location_type}</span>
                  <span className="font-medium">{job.salary}</span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
        {!loading && jobs.length === 0 && (
          <p className="text-center text-slateink py-20" data-testid="jobs-empty">No roles match your filters yet.</p>
        )}
      </div>
    </div>
  );
}
